/**
 * 計測するマーカーのお知らせの照合（風のシートの「計測」タブ、docs/08 §200）。
 *
 * 大会本部がターゲットのタスクの後に配信する「Measuring team will measure
 * following Markers. #1 #2 …」と、選んだチーム（ゼッケン番号）を照らし合わせ、
 * タスクごとに計測の対象に入ったか（✓）入らなかったか（✗）を並べる。
 * - 選べるのは最大5チーム。1番目が自分のチーム（★）、残りは競り合っている相手
 * - 番号は会場ごとに覚える（大会ごとにゼッケンが変わるため）
 * - 競技者一覧（ブリッジが結果サイトから読む）があれば、名前でも選べる
 * - ターゲットが複数あるタスク（HWZ など）は、どれかに入れば ✓。ターゲットごとの行も出す
 *
 * ✓／✗ の意味（利用者の説明、2026-10-11）: ✓ は計測班が計測し、マーカーも回収する。
 * ✗ は計測班の一覧に無い。範囲外に落としていればロガーの記録が成績になる（MMA が
 * 60〜70m あるので有効なこともある）が、範囲内に落としたつもりなら計測班に
 * 見つけてもらえていないので、チームが自分で現地のマーカーを回収しに行く必要がある。
 * そのため自分のチーム（★）が ✗ のタスクは「要確認：マーカーの回収」として目立たせる
 */

import { matchTaskMarkers, type TaskMarkerNotice, type TaskMarkerRow } from '@gv4d/core';

import type { OfficialPibalFeed } from './data/official-pibal-feed.ts';

export const MAX_WATCHED_TEAMS = 5;
const WATCH_KEY_PREFIX = 'gv4d-marker-watch:';

export interface CompetitorEntry {
  no: number;
  name: string;
  from: string;
}

export function loadWatched(venueId: string): number[] {
  try {
    const raw = JSON.parse(localStorage.getItem(WATCH_KEY_PREFIX + venueId) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((n): n is number => Number.isInteger(n) && n > 0).slice(0, MAX_WATCHED_TEAMS) : [];
  } catch {
    return [];
  }
}

function saveWatched(venueId: string, list: number[]): void {
  try {
    localStorage.setItem(WATCH_KEY_PREFIX + venueId, JSON.stringify(list));
  } catch {
    // 保存できなくても今回は効く
  }
}

/**
 * 追加欄の入力を番号にする。「10」「#10」「10 KATAHIRA, Shiro」（一覧から選んだ形）、
 * 名前の一部（「katahira」）のどれでもよい。名前で複数当たるときや見つからなければ null
 */
export function parseTeamInput(raw: string, competitors: CompetitorEntry[]): number | null {
  const text = raw.trim();
  if (!text) return null;
  const num = /^#?\s*(\d{1,4})\b/.exec(text);
  if (num) return Number(num[1]);
  const q = text.toLowerCase();
  const hits = competitors.filter((c) => c.name.toLowerCase().includes(q));
  return hits.length === 1 ? hits[0].no : null;
}

/**
 * 一番新しい計測のお知らせのタスクで、自分のチーム（★）が一覧に無いか
 * （右のボタンの印を「要確認」の色にする）
 */
export function latestOwnTeamOut(notices: TaskMarkerNotice[], venueId: string): boolean {
  const own = loadWatched(venueId)[0];
  const latest = notices[0];
  if (own === undefined || !latest) return false;
  const row = matchTaskMarkers(notices, [own]).find(
    (r) => r.taskNo === latest.taskNo && r.latestSentLocal.slice(0, 10) === latest.sentLocal.slice(0, 10),
  );
  return row?.status.get(own) === 'out';
}

/** 名前の短い形（「KATAHIRA, Shiro」→「KATAHIRA」） */
export function shortName(name: string): string {
  return name.split(',')[0].replace(/\s*\(.*?\)\s*/g, '').trim();
}

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const md = (local: string) => `${Number(local.slice(5, 7))}/${Number(local.slice(8, 10))} ${local.slice(11, 16)}`;

/** 照合の表（DOMに依存しない文字列。テストしやすく） */
export function markerTableHtml(rows: TaskMarkerRow[], watched: number[], names: Map<number, string>): string {
  const head = watched
    .map((no, i) => {
      const name = names.get(no);
      return `<th class="${i === 0 ? 'mk-own' : ''}">${i === 0 ? '★' : ''}#${no}${name ? `<br><small>${escapeHtml(shortName(name))}</small>` : ''}</th>`;
    })
    .join('');
  const targetButton = (target: string | null, label: string) =>
    target
      ? `<button type="button" class="mk-target" data-target="${escapeHtml(target)}" data-label="${escapeHtml(label)}">${escapeHtml(target)}</button>`
      : '—';
  const body = rows
    .map((r) => {
      const label = `Task ${r.taskNo}${r.taskType ? ` ${r.taskType}` : ''}`;
      const local = r.notices.some((n) => (n as { local?: boolean }).local) ? ' <small class="mk-local">貼付</small>' : '';
      const statusCells = watched
        .map((no) =>
          r.status.get(no) === 'in'
            ? '<td class="mk-in" title="計測班が計測・回収">✓</td>'
            : '<td class="mk-out" title="計測班の一覧に番号なし">✗</td>',
        )
        .join('');
      const multi = r.notices.length > 1;
      // 自分のチーム（★）が一覧に無ければ、マーカーを自分で回収しに行く必要があるかもしれない
      const ownOut = watched.length > 0 && r.status.get(watched[0]) === 'out';
      const alert = ownOut ? '<br><span class="mk-alert">要確認：マーカーの回収</span>' : '';
      const first = `<tr class="mk-task${ownOut ? ' mk-own-out' : ''}"><td><b>${escapeHtml(label)}</b>${local}<br><small>${md(r.latestSentLocal)}</small>${alert}</td><td>${
        multi ? `${r.notices.length}か所` : targetButton(r.notices[0].target, label)
      }</td>${statusCells}</tr>`;
      if (!multi) return first;
      const subs = r.notices
        .map(
          (n) =>
            `<tr class="mk-sub"><td></td><td>${targetButton(n.target, label)}</td>${watched
              .map((no) => (n.markers.includes(no) ? '<td class="mk-sub-in">●</td>' : '<td class="mk-sub-out">·</td>'))
              .join('')}</tr>`,
        )
        .join('');
      return first + subs;
    })
    .join('');
  return `<thead><tr><th>タスク</th><th>ターゲット</th>${head}</tr></thead><tbody>${body}</tbody>`;
}

export function setupMarkerPane(options: {
  feed: OfficialPibalFeed;
  venueId: () => string;
  /** ターゲットの座標（例 "1806/0288"）を地図に出す */
  onShowTarget: (coordinates: string, label: string) => void;
}): { render: () => void } {
  const { feed } = options;
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
  const watchBox = $<HTMLDivElement>('mk-watch');
  const input = $<HTMLInputElement>('mk-add-input');
  const datalist = $<HTMLDataListElement>('mk-competitors');
  const status = $<HTMLParagraphElement>('mk-status');
  const table = $<HTMLTableElement>('mk-table');

  let watched = loadWatched(options.venueId());
  let venue = options.venueId();
  const setWatched = (list: number[]) => {
    watched = list.slice(0, MAX_WATCHED_TEAMS);
    saveWatched(options.venueId(), watched);
    render();
  };

  const render = () => {
    if (venue !== options.venueId()) {
      venue = options.venueId();
      watched = loadWatched(venue);
    }
    const competitors = feed.competitors;
    const names = new Map(competitors.map((c) => [c.no, c.name]));
    datalist.replaceChildren(...competitors.map((c) => new Option(`${c.no} ${c.name}${c.from ? `（${c.from}）` : ''}`)));

    watchBox.replaceChildren(
      ...watched.map((no, i) => {
        const chip = document.createElement('span');
        chip.className = `mk-chip${i === 0 ? ' mk-own' : ''}`;
        const name = names.get(no);
        chip.textContent = `${i === 0 ? '★' : ''}#${no}${name ? ` ${shortName(name)}` : ''}`;
        chip.title = i === 0 ? '自分のチーム' : '比べるチーム';
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'mk-chip-x';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `#${no}を外す`);
        remove.onclick = () => setWatched(watched.filter((n) => n !== no));
        chip.append(remove);
        if (i > 0) {
          const own = document.createElement('button');
          own.type = 'button';
          own.className = 'mk-chip-x';
          own.textContent = '★';
          own.title = '自分のチームにする';
          own.onclick = () => setWatched([no, ...watched.filter((n) => n !== no)]);
          chip.append(own);
        }
        return chip;
      }),
    );
    input.disabled = watched.length >= MAX_WATCHED_TEAMS;
    input.placeholder = input.disabled ? `最大${MAX_WATCHED_TEAMS}チームまで` : '番号か名前（例: 10・KATAHIRA）';

    const notices = feed.markerNotices;
    status.classList.remove('warn');
    if (watched.length === 0) {
      status.textContent = `比べるチームの番号を追加してください（最大${MAX_WATCHED_TEAMS}つ。最初が自分のチーム）`;
      table.innerHTML = '';
      return;
    }
    if (notices.length === 0) {
      status.textContent = 'まだ計測のお知らせがありません';
      table.innerHTML = '';
      return;
    }
    const rows = matchTaskMarkers(notices, watched);
    const own = watched[0];
    const outs = rows.filter((r) => r.status.get(own) === 'out');
    status.textContent =
      `計測のお知らせ ${rows.length}タスク（最終 ${md(rows[0].latestSentLocal)}）・` +
      (outs.length === 0
        ? `★#${own} はすべて計測班が計測・回収`
        : `★#${own} は ${outs.map((r) => `Task ${r.taskNo}`).join('・')} で一覧に番号なし → 範囲内に落としたなら、マーカーの回収が必要か確認`);
    status.classList.toggle('warn', outs.length > 0);
    table.innerHTML = markerTableHtml(rows, watched, names);
  };

  const add = () => {
    const no = parseTeamInput(input.value, feed.competitors);
    if (no === null) {
      status.textContent = '番号が分かりませんでした（番号か、名前の一部を入れてください）';
      return;
    }
    input.value = '';
    if (!watched.includes(no)) setWatched([...watched, no]);
  };
  $<HTMLButtonElement>('mk-add').onclick = add;
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') add();
  });
  // 一覧（datalist）から選んだら、そのまま追加する
  input.addEventListener('change', () => {
    if (/^\d{1,4} /.test(input.value)) add();
  });
  table.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button.mk-target');
    if (b) options.onShowTarget(b.dataset.target ?? '', b.dataset.label ?? '');
  });

  return { render };
}
